import React, {useEffect, useMemo, useState} from 'react';
import {Badge, Menu, Select, Tag} from 'antd';
import {Link, useLocation} from 'react-router-dom';
import {
    AppleOutlined,
    AuditOutlined,
    BarChartOutlined,
    BookOutlined,
    DollarCircleOutlined,
    FolderOpenOutlined,
    GiftOutlined,
    HomeOutlined,
    NotificationOutlined,
    StarOutlined,
    TranslationOutlined,
    UsergroupAddOutlined,
    VideoCameraOutlined,
    CommentOutlined,
    CheckCircleOutlined,
    LinkOutlined,
    LineChartOutlined,
    AreaChartOutlined,
    SmileOutlined,
} from '@ant-design/icons';
import {config, getCookie} from '../helpers';

const {SubMenu} = Menu;
const {Option} = Select;

const PATH_ALIASES = [
    [/^\/recipe\//, '/recipes'],
    [/^\/user\//, '/users'],
    [/^\/ingredient\//, '/ingredients'],
    [/^\/training-videos\//, '/training-videos'],
    [/^\/competitions\//, '/competitions'],
    [/^\/bingos?\//, '/bingos'],
    [/^\/bingo\//, '/bingos'],
    [/^\/notifications\//, '/notifications'],
    [/^\/translations\//, '/translations'],
    [/^\/task\//, '/tasks'],
    [/^\/advent-calendar\//, '/advent-calendar'],
    [/^\/menu-templates\//, '/menu-templates'],
    [/^\/ingredients\/substitute-groups\//, '/ingredients/substitute-groups'],
    [/^\/ingredients\/allergens\//, '/ingredients/allergens'],
    [/^\/ingredients\/custom-units\//, '/ingredients/custom-units'],
];

const KEY_TO_SUBMENU = {
    '/users': 'users',
    '/start-guides': 'users',
    '/levels': 'users',
    '/actions': 'users',
    '/badges': 'users',
    '/invoices': 'payment',
    '/cancel-retention-discounts': 'payment',
    '/recipes': 'recipes',
    '/recipe-messages': 'recipes',
    '/create-recipe': 'recipes',
    '/ingredients': 'ingredients',
    '/ingredients/freeware': 'ingredients',
    '/ingredients/substitute-groups': 'ingredients',
    '/ingredients/allergens': 'ingredients',
    '/ingredients/custom-units': 'ingredients',
    '/selver': 'ingredients',
    '/menu-templates': 'content',
    '/notifications': 'notifications',
    '/notifications/create': 'notifications',
    '/content/categories': 'content',
    '/tags': 'content',
    '/menu-links': 'content',
    '/generate-blog': 'content',
    '/exercises': 'content',
    '/competitions': 'events',
    '/bingos': 'events',
    '/advent-calendar': 'events',
    '/receipts': 'events',
    '/banners': 'events',
    '/training-videos': 'videos',
    '/courses': 'videos',
    '/comments': 'reports',
    '/tasks': 'reports',
    '/short-url': 'reports',
    '/app-visited': 'reports',
    '/active-inactive': 'reports',
    '/wellness': 'reports',
    '/food-scans': 'reports',
    '/event-analytics': 'reports',
    '/error-log': 'logs',
    '/admin-logs': 'logs',
};

const resolveSelectedKey = (pathname) => {
    if (pathname === '/') {
        return '/';
    }

    for (const [pattern, key] of PATH_ALIASES) {
        if (pattern.test(pathname)) {
            return key;
        }
    }

    return pathname;
};

const AdminNavMenu = ({onSiteChange}) => {
    const location = useLocation();
    const selectedKey = useMemo(
        () => resolveSelectedKey(location.pathname),
        [location.pathname],
    );

    const [openKeys, setOpenKeys] = useState([]);

    useEffect(() => {
        const submenu = KEY_TO_SUBMENU[selectedKey];
        if (submenu) {
            setOpenKeys(prev => (prev.includes(submenu) ? prev : [...prev, submenu]));
        }
    }, [selectedKey]);

    return (
        <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
        >
            <Menu.Item className="site-select" key="site">
                <Select
                    onChange={onSiteChange}
                    defaultValue={getCookie('api_url') ? getCookie('api_url') : config('api.url')}
                    style={{width: '100%'}}
                >
                    <Option value="https://api.fitlap.ee/">
                        Fitlap.ee <Badge style={{fontSize: 10}} count="LIVE"/>
                    </Option>
                    <Option value="https://api.fitlap.com/">
                        Fitlap.com <Badge style={{fontSize: 10}} count="LIVE"/>
                    </Option>
                    <Option value="https://api2.fitlap.fi/">
                        Fitlap.fi <Badge style={{fontSize: 10}} count="LIVE"/>
                    </Option>
                    <Option value="https://api.fitlap.in/">Fitlap.in</Option>
                    <Option value="http://127.0.0.1:8080/">Localhost</Option>
                </Select>
            </Menu.Item>

            <Menu.Item key="/" icon={<HomeOutlined/>}>
                <Link to="/">Dashboard</Link>
            </Menu.Item>

            <SubMenu key="users" icon={<UsergroupAddOutlined/>} title="Users">
                <Menu.Item key="/users"><Link to="/users">Users</Link></Menu.Item>
                <Menu.Item key="/start-guides"><Link to="/start-guides">Start guides</Link></Menu.Item>
                <Menu.Item key="/levels"><Link to="/levels">Levels</Link></Menu.Item>
                <Menu.Item key="/actions"><Link to="/actions">Actions</Link></Menu.Item>
                <Menu.Item key="/badges"><Link to="/badges">Decorations</Link></Menu.Item>
            </SubMenu>

            <SubMenu key="payment" icon={<DollarCircleOutlined/>} title="Payment">
                <Menu.Item key="/invoices"><Link to="/invoices">Invoices</Link></Menu.Item>
                <Menu.Item key="/cancel-retention-discounts">
                    <Link to="/cancel-retention-discounts">
                        Cancel discounts <Tag color="success">New</Tag>
                    </Link>
                </Menu.Item>
            </SubMenu>

            <SubMenu key="recipes" icon={<BookOutlined/>} title="Recipes">
                <Menu.Item key="/recipes"><Link to="/recipes">All recipes</Link></Menu.Item>
                <Menu.Item key="/recipe-messages"><Link to="/recipe-messages">Category messages</Link></Menu.Item>
                <Menu.Item key="/create-recipe"><Link to="/create-recipe">Create recipe</Link></Menu.Item>
            </SubMenu>

            <SubMenu key="ingredients" icon={<AppleOutlined/>} title="Ingredients">
                <Menu.Item key="/ingredients"><Link to="/ingredients">All ingredients</Link></Menu.Item>
                <Menu.Item key="/ingredients/freeware"><Link to="/ingredients/freeware">Freeware</Link></Menu.Item>
                <Menu.Item key="/ingredients/substitute-groups">
                    <Link to="/ingredients/substitute-groups">Substitute Groups</Link>
                </Menu.Item>
                <Menu.Item key="/ingredients/allergens"><Link to="/ingredients/allergens">Allergens</Link></Menu.Item>
                <Menu.Item key="/ingredients/custom-units">
                    <Link to="/ingredients/custom-units">Custom Units</Link>
                </Menu.Item>
                <Menu.Item key="/selver"><Link to="/selver">Selver</Link></Menu.Item>
            </SubMenu>

            <SubMenu key="events" icon={<StarOutlined/>} title="Events & campaigns">
                <Menu.Item key="/banners"><Link to="/banners">Adv. banners</Link></Menu.Item>
                <Menu.Item key="/competitions"><Link to="/competitions">Competitions</Link></Menu.Item>
                <Menu.Item key="/bingos"><Link to="/bingos">Bingos</Link></Menu.Item>
                <Menu.Item key="/advent-calendar"><Link to="/advent-calendar">Advent Calendar</Link></Menu.Item>
                <Menu.Item key="/receipts"><Link to="/receipts">Receipt campaign</Link></Menu.Item>
            </SubMenu>

            <Menu.Divider />

            <SubMenu key="content" icon={<FolderOpenOutlined/>} title="Content">
                <Menu.Item key="/content/categories"><Link to="/content/categories">Categories</Link></Menu.Item>
                <Menu.Item key="/tags"><Link to="/tags">Tags</Link></Menu.Item>
                <Menu.Item key="/menu-templates"><Link to="/menu-templates">Menu Templates</Link></Menu.Item>
                <Menu.Item key="/exercises"><Link to="/exercises">Training exercises</Link></Menu.Item>
                <Menu.Item key="/generate-blog">
                    <Link to="/generate-blog">AI blog post</Link>
                </Menu.Item>
                <Menu.Item key="/menu-links">
                    <Link to="/menu-links">Menu links <Tag color="success">New</Tag></Link>
                </Menu.Item>
            </SubMenu>

            <SubMenu key="videos" icon={<VideoCameraOutlined/>} title="Videos">
                <Menu.Item key="/training-videos"><Link to="/training-videos">Training videos</Link></Menu.Item>
                <Menu.Item key="/courses"><Link to="/courses">Courses & podcast</Link></Menu.Item>
            </SubMenu>

            <SubMenu key="reports" icon={<BarChartOutlined/>} title={<span>Analytics <Tag color="success">New</Tag></span>}>
                <Menu.Item key="/event-analytics">
                    <Link to="/event-analytics">Events <Tag color="success">New</Tag></Link>
                </Menu.Item>
                <Menu.Item key="/food-scans">
                    <Link to="/food-scans">Food scans</Link>
                </Menu.Item>
                <Menu.Item key="/comments"><Link to="/comments">Comments</Link></Menu.Item>
                <Menu.Item key="/tasks"><Link to="/tasks">Daily tasks</Link></Menu.Item>
                <Menu.Item key="/short-url"><Link to="/short-url">Short URLs</Link></Menu.Item>
                <Menu.Item key="/app-visited"><Link to="/app-visited">App visited</Link></Menu.Item>
                <Menu.Item key="/active-inactive">
                    <Link to="/active-inactive">Active/Inactive customers</Link>
                </Menu.Item>
                <Menu.Item key="/wellness"><Link to="/wellness">Wellness</Link></Menu.Item>
            </SubMenu>

            <Menu.Divider />

            <SubMenu key="notifications" icon={<NotificationOutlined/>} title="Notifications">
                <Menu.Item key="/notifications"><Link to="/notifications">Notifications</Link></Menu.Item>
                <Menu.Item key="/notifications/create">
                    <Link to="/notifications/create">New Notification</Link>
                </Menu.Item>
            </SubMenu>

            <Menu.Item key="/translations" icon={<TranslationOutlined/>}>
                <Link to="/translations">Translations</Link>
            </Menu.Item>

            <SubMenu key="logs" icon={<AuditOutlined/>} title="Logs">
                <Menu.Item key="/error-log"><Link to="/error-log">Error Log</Link></Menu.Item>
                <Menu.Item key="/admin-logs"><Link to="/admin-logs">Admin Logs</Link></Menu.Item>
            </SubMenu>
        </Menu>
    );
};

export default AdminNavMenu;
